import {
  HeadlineKpis,
  SegmentParams,
  SHARE_SUM_EPSILON,
  SimulationRequest,
  SimulationResult,
  YearUtilisation,
} from '../models/site-simulation.model';

/**
 * Pure Monte-Carlo engine for the Auslastungsprognose. Side-effect-free and deterministic
 * given its `random` source, so it can be tested against the spec's §6 identities in
 * isolation. A run fans out into two segment simulations (onRoute, destination) that share
 * the demand inputs, differ by Zeitfenster / Turn-in-Rate / per-session energy, and are
 * blended by the location's split (spec §2.2, §4.2–§4.6).
 */

/** Fixed run constants, not user-editable. */
export const SIMULATION_CONSTANTS = {
  /** Base year is 1, not 0. */
  HORIZON_YEARS: 10,
  ITERATIONS: 10000,
} as const;

/** Folded into every segment's E_max ceiling; a client-set default. */
export const AVAILABILITY = 0.997;

/** Per-session energy per segment archetype, in kWh; client-set defaults. */
export const SEGMENT_ENERGY = { onRoute: 33, destination: 26 } as const;

/**
 * E_max (spec §4.4): the most a segment's charge points could deliver in a day. Single
 * source of truth for the engine's utilisation denominator and the view's "of max" site
 * total, so the two cannot drift. Returns `0` when any factor is `0`; callers' `eMax > 0 ?`
 * guards turn that into a `0` KPI rather than a division by zero.
 */
export function segmentEnergyCeilingKwhDay(
  lp: number,
  powerKw: number,
  windowHours: number,
): number {
  return lp * powerKw * windowHours * AVAILABILITY;
}

/**
 * Share-weighted across both segments. The view shows `used = combinedUtil × this`, which is
 * what keeps the KPI card consistent with the averaged utilisation ratio.
 */
export function combinedEnergyCeilingKwhDay(
  request: Pick<
    SimulationRequest,
    | 'lp'
    | 'powerKw'
    | 'onRoute'
    | 'destination'
    | 'destinationShare'
    | 'onRouteShare'
  >,
): number {
  const { lp, powerKw, onRoute, destination, destinationShare, onRouteShare } =
    request;
  return (
    destinationShare *
      segmentEnergyCeilingKwhDay(lp, powerKw, destination.windowHours) +
    onRouteShare * segmentEnergyCeilingKwhDay(lp, powerKw, onRoute.windowHours)
  );
}

/**
 * Inverse-transform sample of a triangular distribution on a uniform `u ∈ [0, 1)`. Requires
 * `low ≤ mode ≤ high`, which the host's `inputsValid` gate enforces upstream; `high ≤ low`
 * returns `low`, so every draw of a degenerate input agrees.
 */
export function sampleTriangular(
  low: number,
  mode: number,
  high: number,
  u: number,
): number {
  if (high <= low) {
    return low;
  }
  const span = high - low;
  const c = (mode - low) / span;
  if (u < c) {
    return low + Math.sqrt(u * span * (mode - low));
  }
  return high - Math.sqrt((1 - u) * span * (high - mode));
}

/** Mulberry32, so tests and the seeded mock path reproduce a run exactly. */
export function createSeededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Linear-interpolated percentile of an ascending-sorted array (`p ∈ [0, 1]`). */
function percentile(sortedAsc: readonly number[], p: number): number {
  const n = sortedAsc.length;
  if (n === 0) {
    return 0;
  }
  if (n === 1) {
    return sortedAsc[0];
  }
  const idx = p * (n - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const frac = idx - lo;
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * frac;
}

/** The demand-side inputs shared by both segments of a run. */
type SharedInputs = Pick<
  SimulationRequest,
  'dtv' | 'bevShare' | 'bevGrowth' | 'lp' | 'powerKw'
>;

/**
 * Draws are pulled from `random` sequentially, so a seeded source reproduces a run exactly.
 * Divisions by `LP` and `E_max` are guarded to `0` so the engine stays total under direct
 * test calls, even though the upstream `inputsValid` gate forbids `lp < 1` / `kW ≤ 0`.
 */
export function runSegment(
  shared: SharedInputs,
  segment: SegmentParams,
  kwhPerSession: number,
  random: () => number = Math.random,
): SimulationResult {
  const { dtv, bevShare, bevGrowth, lp, powerKw } = shared;
  const { captureFactor, windowHours } = segment;
  const { HORIZON_YEARS, ITERATIONS } = SIMULATION_CONSTANTS;

  // 0 here disables the utilisation KPI through the guards below.
  const eMax = segmentEnergyCeilingKwhDay(lp, powerKw, windowHours);

  const sessionsByYear: number[][] = Array.from(
    { length: HORIZON_YEARS },
    () => [],
  );
  const energyByYear: number[][] = Array.from(
    { length: HORIZON_YEARS },
    () => [],
  );

  for (let i = 0; i < ITERATIONS; i++) {
    // Once per iteration, not per year: a year's draw must not resample the inputs.
    const dtvI = sampleTriangular(dtv.low, dtv.mode, dtv.high, random());
    const share0 = sampleTriangular(
      bevShare.low,
      bevShare.mode,
      bevShare.high,
      random(),
    );
    const growth = sampleTriangular(
      bevGrowth.low,
      bevGrowth.mode,
      bevGrowth.high,
      random(),
    );
    for (let y = 1; y <= HORIZON_YEARS; y++) {
      // Compounded over the year index, base year 1.
      const shareY = share0 * Math.pow(1 + growth, y - 1);
      const bevPass = dtvI * shareY; // passing BEV/day (spec §4.2)
      const sessions = bevPass * captureFactor; // raw sessions/day
      const energy = sessions * kwhPerSession; // kWh/day
      sessionsByYear[y - 1].push(sessions);
      energyByYear[y - 1].push(energy);
    }
  }

  const years: YearUtilisation[] = [];
  for (let y = 1; y <= HORIZON_YEARS; y++) {
    const sessions = [...sessionsByYear[y - 1]].sort((a, b) => a - b);
    const energy = [...energyByYear[y - 1]].sort((a, b) => a - b);
    const energyP50 = percentile(energy, 0.5);
    // Dividing the sorted energies by the positive constant eMax is order-preserving, so
    // percentile(energy / eMax) === percentile(energy) / eMax (spec §4.5).
    const utilAt = (p: number): number =>
      eMax > 0 ? percentile(energy, p) / eMax : 0;
    years.push({
      year: y,
      // Single representative curve, from the mode inputs rather than the samples.
      bevSharePct: bevShare.mode * Math.pow(1 + bevGrowth.mode, y - 1),
      sessionsP10: percentile(sessions, 0.1),
      sessionsP50: percentile(sessions, 0.5),
      sessionsP90: percentile(sessions, 0.9),
      kwhPerCp: lp > 0 ? energyP50 / lp : 0,
      energyUtilisationP10: utilAt(0.1),
      energyUtilisationP50: utilAt(0.5),
      energyUtilisationP90: utilAt(0.9),
    });
  }

  // Status-quo point estimate: deterministic year-1 from the mode inputs, no sampling and
  // no growth. The percentile bands in `years` still come from the sampled run.
  const sessionsSq = dtv.mode * bevShare.mode * captureFactor;
  const energySq = sessionsSq * kwhPerSession;
  const headline: HeadlineKpis = {
    energyUtilisationPct: eMax > 0 ? energySq / eMax : 0,
    kwhPerLpDay: lp > 0 ? energySq / lp : 0,
    sessionsPerLpDay: lp > 0 ? sessionsSq / lp : 0,
    avgKwhPerSession: sessionsSq > 0 ? energySq / sessionsSq : 0,
  };

  return { headline, years };
}

/**
 * Both segments always have equal horizon length, so `year` and the mode-derived
 * `bevSharePct` survive the average unchanged. Weighted-averaging each segment's *marginal*
 * percentiles keeps the blended band comonotonic and order-preserving — it is deliberately
 * not a per-iteration sum.
 */
export function combineResults(
  destination: SimulationResult,
  onRoute: SimulationResult,
  destinationShare: number,
  onRouteShare: number,
): SimulationResult {
  const blend = (d: number, o: number): number =>
    destinationShare * d + onRouteShare * o;
  const headline: HeadlineKpis = {
    energyUtilisationPct: blend(
      destination.headline.energyUtilisationPct,
      onRoute.headline.energyUtilisationPct,
    ),
    kwhPerLpDay: blend(
      destination.headline.kwhPerLpDay,
      onRoute.headline.kwhPerLpDay,
    ),
    sessionsPerLpDay: blend(
      destination.headline.sessionsPerLpDay,
      onRoute.headline.sessionsPerLpDay,
    ),
    avgKwhPerSession: blend(
      destination.headline.avgKwhPerSession,
      onRoute.headline.avgKwhPerSession,
    ),
  };
  const years: YearUtilisation[] = destination.years.map((d, i) => {
    const o = onRoute.years[i];
    return {
      year: d.year,
      bevSharePct: blend(d.bevSharePct, o.bevSharePct),
      sessionsP10: blend(d.sessionsP10, o.sessionsP10),
      sessionsP50: blend(d.sessionsP50, o.sessionsP50),
      sessionsP90: blend(d.sessionsP90, o.sessionsP90),
      kwhPerCp: blend(d.kwhPerCp, o.kwhPerCp),
      energyUtilisationP10: blend(
        d.energyUtilisationP10,
        o.energyUtilisationP10,
      ),
      energyUtilisationP50: blend(
        d.energyUtilisationP50,
        o.energyUtilisationP50,
      ),
      energyUtilisationP90: blend(
        d.energyUtilisationP90,
        o.energyUtilisationP90,
      ),
    };
  });
  return { headline, years };
}

/**
 * Segments with a positive share are simulated destination-first on the **same** `random`
 * stream, so a seeded source reproduces the run exactly. A lone segment runs from the
 * stream's start and is returned unblended, keeping the pure-segment identity exact.
 *
 * The shares must sum to 1 within {@link SHARE_SUM_EPSILON}. The split dialogs guarantee
 * that by complement-coupling; the engine rejects a deviating sum so another caller fails
 * loudly rather than receiving a silently under-weighted blend.
 */
export function runSimulation(
  request: SimulationRequest,
  random: () => number = Math.random,
): SimulationResult {
  const { onRoute, destination, destinationShare, onRouteShare } = request;
  const shareSum = destinationShare + onRouteShare;
  if (Math.abs(shareSum - 1) > SHARE_SUM_EPSILON) {
    throw new Error(
      `site-simulation: onRoute/destination shares must sum to 1 (got ${destinationShare} + ${onRouteShare} = ${shareSum}); the split dialogs guarantee this — a deviating sum means an unvalidated caller.`,
    );
  }
  const runDestination = destinationShare > 0;
  const runOnRoute = onRouteShare > 0;

  const destResult = runDestination
    ? runSegment(request, destination, SEGMENT_ENERGY.destination, random)
    : null;
  const onRouteResult = runOnRoute
    ? runSegment(request, onRoute, SEGMENT_ENERGY.onRoute, random)
    : null;

  if (destResult && onRouteResult) {
    return combineResults(
      destResult,
      onRouteResult,
      destinationShare,
      onRouteShare,
    );
  }
  // The survivor's share is 1, so returning it unblended equals a standalone `runSegment`.
  return (destResult ?? onRouteResult) as SimulationResult;
}
